import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown, false = unauth, object = auth
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    // CRITICAL: If returning from Emergent Google Auth callback, skip the /me probe.
    // AuthCallback will exchange the session_id first and then trigger refresh().
    if (typeof window !== "undefined" && window.location.hash && window.location.hash.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  // Sync the native-push token to the backend every time the authenticated
  // user changes (login, register, cold start when already authed). This
  // repairs the common bug where the FCM/APNs token was received by the
  // native layer before the user had logged in — those tokens were being
  // POSTed with no auth header and silently 401-ing, so no push ever landed.
  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const m = await import("@/lib/native-push");
        await m.syncNativePushToken();
      } catch { /* ignore — web platform or not installed */ }
    })();
  }, [user?.id]);

  const login = async (identifier, password) => {
    try {
      const { data } = await api.post("/auth/login", { identifier, password });
      try { if (data?.token) localStorage.setItem("kh_token", data.token); } catch { /* ignore */ }
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      // Fallback: if the identifier looks like a phone and password is a short PIN,
      // try the taxi-driver login endpoint. Drivers use phone+PIN instead of a regular account.
      const looksLikePhone = /^[+\d][\d\s-]{6,}$/.test((identifier || "").trim());
      const looksLikePin = /^\d{3,8}$/.test((password || "").trim());
      if (looksLikePhone && looksLikePin) {
        try {
          const phone = identifier.trim();
          const { data: drv } = await api.post("/taxi/driver/login", { phone, pin: password.trim() });
          if (drv?.id) {
            try {
              localStorage.setItem(
                "kh_taxi_driver_session",
                JSON.stringify({ id: drv.id, name: drv.name, taxi_type: drv.taxi_type })
              );
            } catch { /* ignore */ }
            // Return a virtual user marking this as a driver session — the login page uses this to redirect.
            const virtualUser = { id: drv.id, name: drv.name, role: "taxi_driver", _isTaxiDriver: true };
            return { ok: true, user: virtualUser, isTaxiDriver: true };
          }
        } catch { /* fall through to the original error */ }
      }
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      try { if (data?.token) localStorage.setItem("kh_token", data.token); } catch { /* ignore */ }
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    let device_token = null;
    try { device_token = localStorage.getItem("native_push_token"); } catch { /* ignore */ }
    try { await api.post("/auth/logout", device_token ? { device_token } : {}); } catch { /* ignore */ }
    try { localStorage.removeItem("kh_token"); } catch { /* ignore */ }
    try { localStorage.removeItem("native_push_token"); } catch { /* ignore */ }
    try { localStorage.removeItem("native_push_platform"); } catch { /* ignore */ }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
